/*
 * Shared sign in across the brands' addresses (auth/sso.ts on the server). Sign in at one Steward and the others open
 * signed in on their next page load:
 *
 * - A page with no session first asks head office's address (it holds this browser's sign in), once per page load. It
 *   comes back through /handoff signed in, or with #sso=none and the sign in page shows.
 * - Right after signing in at a brand's address, the page signs in at head office's address too and comes straight back.
 * - Signing out here stops the asking at this address until the next sign in, so signing out sticks.
 */
import { api } from "../api/client";

const SIGNED_OUT = "steward.signedOut";
let state: "idle" | "leaving" | "done" = "idle";

function signedOutHere(): boolean {
  try {
    return localStorage.getItem(SIGNED_OUT) === "1";
  } catch {
    return false;
  }
}

/** Signing out at this address: no shared sign in here until someone signs in here again. */
export function markSignedOut(): void {
  state = "done";
  try {
    localStorage.setItem(SIGNED_OUT, "1");
  } catch {
    // Private windows can refuse storage; head office's session ended with this one anyway.
  }
}

/** Signed in at this address (by hand or a code): shared sign in is on again. */
export function markSignedIn(): void {
  try {
    localStorage.removeItem(SIGNED_OUT);
  } catch {
    // Nothing to undo.
  }
}

/** This page load is done asking (the code didn't work), so the sign in page shows. */
export function stopSso(): void {
  state = "done";
}

/**
 * With no session: goes to head office's address to borrow its sign in, once per page load. Answers true while the page
 * is on its way there (show a spinner, not the sign in form).
 */
export function leavingForSso(hub: string | null | undefined): boolean {
  if (state === "idle") {
    const { origin, pathname, search, hash } = window.location;
    if (hash.includes("sso=none")) window.history.replaceState(null, "", `${pathname}${search}`);
    if (__PREVIEW__ || !hub || hub === origin || hash.includes("sso=none") || signedOutHere()) state = "done";
    else {
      state = "leaving";
      window.location.replace(`${hub}/v1/auth/sso?to=${encodeURIComponent(`${origin}${pathname}${search}`)}`);
    }
  }
  return state === "leaving";
}

/** Right after a full sign in: signs in at head office's address too, then comes back here. Else runs `go`. */
export async function afterFullSignIn(go: () => Promise<unknown>): Promise<void> {
  markSignedIn();
  try {
    const { url } = await api<{ url: string | null }>("POST", "auth/sso/seed", { back: `${window.location.origin}/` });
    if (url) {
      window.location.replace(url);
      return;
    }
  } catch {
    // Shared sign in is a nice extra: this address works either way.
  }
  await go();
}

/** A same-address page to open after a code sign in, never another site. Sign in pages open the app instead. */
export function safeNext(next: string | null): string {
  if (!next?.startsWith("/") || next.startsWith("//") || next.includes("\\")) return "/";
  if (next.startsWith("/sign-in") || next.startsWith("/handoff")) return "/";
  return next;
}
